import { useCallback, useEffect, useRef, useState } from "react"
import { useWidget } from "../../../providers/widget"
import { useFetchWidget } from "../../../hooks"
import { PropertyMapWidgetConfig } from "../config"
import { useFetchPropertyForMap, PropertyMapData } from "../data"
import WidgetDataError from "../../../components/WidgetDataError"
import { useLeaflet } from "../../../common/map"
import { getMarkerBadgeHtml, getPopupHtml } from "../../../common/map"
import { getProxiedImageUrl } from "../../../common/image-proxy"
import { resolvePropertyHref } from "../../../common/property-href"
import { usePropertyHrefFeed } from "../../../common/use-property-href-feed"
import { resolveLeafletTileConfig } from "../../explorer/components/map/tile-layer"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const L: any

export function PropertyMapContent() {
  const { options, feedId, widgetId } = useWidget<PropertyMapWidgetConfig>()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  // Feed-global explorer map styling (#1184): the property map inherits the
  // explorer's tileProvider/mapboxPublicToken/mapboxStyle LIVE from the feed
  // blob (never per-widget settings — the #1046 presentation rule), so a
  // feed-level token rotation or style edit reaches this widget with no
  // widget save. Read raw off the feed like pdpRoute above; the resolver
  // validates token/style shapes itself.
  const feedExplorerMap = widgetData?.feed?.additional_info?.explorer?.map as
    Parameters<typeof resolveLeafletTileConfig>[0]
  const leafletLoaded = useLeaflet()
  // One-shot fallback to OSM when Mapbox tiles fail asynchronously (revoked
  // token, missing style scope — those only surface as 401/404 tile errors).
  // Keyed to the exact failing config so an edited provider/token/style gets
  // a fresh chance instead of staying pinned to OSM for the component's life.
  const [failedTileConfig, setFailedTileConfig] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tileLayerRef = useRef<any>(null)

  const mapContainerRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null)
  const activePopupRef = useRef<HTMLDivElement | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const markerRef = useRef<any>(null)

  const filter = {
    feed_id: options.filter.feed_id ?? feedId,
    property: options.filter.property,
    platform: options.filter.platform,
  }
  const { data: property, isLoading, isError, error } = useFetchPropertyForMap(filter)

  const hasLocation = !!property?.location?.lat && !!property?.location?.lng

  const closePopup = useCallback(() => {
    if (activePopupRef.current) {
      activePopupRef.current.remove()
      activePopupRef.current = null
    }
    if (markerRef.current?._icon) {
      (markerRef.current._icon as HTMLElement).classList.remove('hfse-map-badge-parent-focus')
    }
  }, [])

  const positionPopup = useCallback((container: HTMLElement) => {
    if (!mapRef.current || !markerRef.current || !mapContainerRef.current) return
    const markerPoint = mapRef.current.latLngToContainerPoint(markerRef.current.getLatLng())
    const mapWidth = mapContainerRef.current.offsetWidth
    const mapHeight = mapContainerRef.current.offsetHeight
    const tooltipWidth = 300
    const tooltipHeight = 280
    const gap = 20

    // Place to the right of the marker; if not enough room, place to the left
    const placeRight = markerPoint.x + gap + tooltipWidth < mapWidth
    const left = placeRight
      ? markerPoint.x + gap
      : markerPoint.x - gap - tooltipWidth

    // Vertically center on marker, clamp within map bounds
    let top = markerPoint.y - tooltipHeight / 2
    top = Math.max(10, Math.min(top, mapHeight - tooltipHeight - 10))

    container.style.left = `${left}px`
    container.style.top = `${top}px`
  }, [])

  const updatePopupPosition = useCallback(() => {
    if (!activePopupRef.current) return
    positionPopup(activePopupRef.current)
  }, [positionPopup])

  const showPopup = useCallback((property: PropertyMapData) => {
    if (!mapRef.current || !markerRef.current || !mapContainerRef.current) return

    closePopup()

    const el = markerRef.current._icon as HTMLElement | undefined
    if (el) el.classList.add('hfse-map-badge-parent-focus')

    const imageUrl = property.images?.[0]
      ? getProxiedImageUrl(property.images[0], 300, 200)
      : ''

    // #1714: a property the feed maps to its own page (External Property Pages) links there.
    const pdpUrl = property.slug ? resolvePropertyHref({ feed: hrefFeed, slug: property.slug, pdpRoute }) : '#'
    const popupHtml = getPopupHtml(property, imageUrl, pdpUrl)

    const container = document.createElement('div')
    container.innerHTML = popupHtml
    container.style.position = 'absolute'
    container.style.zIndex = '1000002'
    container.style.pointerEvents = 'auto'

    positionPopup(container)

    const closeBtn = container.querySelector('.hfse-map-tooltip-close-btn')
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        closePopup()
      })
    }

    mapContainerRef.current.appendChild(container)
    activePopupRef.current = container
  }, [closePopup, hrefFeed, pdpRoute, positionPopup])

  // Initialize map once Leaflet is ready and we have location data
  useEffect(() => {
    if (!leafletLoaded || !hasLocation || !mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, {
      zoom: options.zoom,
      center: [property!.location.lat, property!.location.lng],
      zoomControl: false,
      // OSM's license (and Mapbox's ToS) require visible attribution; the
      // tile layer effect below supplies the provider-correct string (#1184).
      attributionControl: true,
    })
    map.attributionControl.setPrefix(false)

    mapRef.current = map

    setTimeout(() => map.invalidateSize(), 100)

    map.on('move', updatePopupPosition)
    map.on('zoom', updatePopupPosition)

    return () => {
      map.remove()
      mapRef.current = null
      tileLayerRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafletLoaded, hasLocation])

  // Tile layer lives in its own effect: the feed-global styling rides the
  // widget fetch and can resolve AFTER map init, and live edits to
  // provider/token/style must swap the layer instead of stranding the one
  // created at init (#1184 — mirrors the explorer's #852 wiring).
  const tileConfigSignature = `${feedExplorerMap?.tileProvider ?? ''}|${feedExplorerMap?.mapboxPublicToken ?? ''}|${feedExplorerMap?.mapboxStyle ?? ''}`
  const resolvedTileProvider = resolveLeafletTileConfig(feedExplorerMap, failedTileConfig === tileConfigSignature).provider
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current) return
    const config = resolveLeafletTileConfig(feedExplorerMap, failedTileConfig === tileConfigSignature)
    const layer = L.tileLayer(config.url, { attribution: config.attribution })
    if (config.provider === 'mapbox') {
      layer.once('tileerror', () => setFailedTileConfig(tileConfigSignature))
    }
    if (tileLayerRef.current) {
      tileLayerRef.current.off('tileerror')
      tileLayerRef.current.remove()
    }
    layer.addTo(mapRef.current)
    tileLayerRef.current = layer
  // hasLocation gates map creation above, so it must re-trigger this effect.
  }, [leafletLoaded, hasLocation, tileConfigSignature, failedTileConfig]) // eslint-disable-line react-hooks/exhaustive-deps

  // Place marker when property data arrives
  useEffect(() => {
    if (!mapRef.current || !leafletLoaded || !property) return
    if (!property.location?.lat || !property.location?.lng) return

    // Remove previous marker
    if (markerRef.current) {
      markerRef.current.remove()
      markerRef.current = null
    }
    closePopup()

    const { lat, lng } = property.location

    const badgeHtml = getMarkerBadgeHtml(property, options.showPrice)
    const marker = L.marker([lat, lng], {
      icon: L.divIcon({
        html: badgeHtml,
        iconSize: [60, 40],
        iconAnchor: [30, 20],
        className: 'hfse-map-marker-wrapper',
      }),
    })

    marker.on('click', () => {
      if (activePopupRef.current) {
        closePopup()
      } else {
        showPopup(property)
      }
    })

    marker.addTo(mapRef.current)
    markerRef.current = marker

    mapRef.current.setView([lat, lng], options.zoom)

    if (options.tooltipOnLoad) {
      showPopup(property)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property, leafletLoaded, closePopup, showPopup])

  const handleZoomIn = () => mapRef.current?.zoomIn()
  const handleZoomOut = () => mapRef.current?.zoomOut()
  const handleFullscreen = () => {
    const el = mapContainerRef.current?.parentElement
    if (!el) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      el.requestFullscreen()
    }
  }

  // Surface a data-fetch failure instead of silently rendering nothing (#740).
  if (isError) {
    return <WidgetDataError error={error} />
  }

  // Don't render anything if data loaded but no location
  if (!isLoading && !hasLocation) {
    return null
  }

  return (
    <div className="hfse-property-map-container" style={{ height: `${options.height}px` }}>
      {isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--hfse-muted-foreground, #999)', position: 'absolute', inset: 0, zIndex: 2 }}>
          Loading map...
        </div>
      )}
      <div className="hfse-map" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

      {/* Mapbox requires its official logo, unaltered, in addition to text
          attribution when its tiles render through Leaflet (#858). The logo
          itself is the CSS background of .hfse-mapbox-logo. */}
      {leafletLoaded && hasLocation && resolvedTileProvider === 'mapbox' && (
        <a
          className="hfse-mapbox-logo"
          href="https://www.mapbox.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Mapbox logo"
        />
      )}

      {leafletLoaded && hasLocation && (
        <div className="hfse-map-controls">
          <div className="hfse-map-controls-left">
            <div className="map-control control-group">
              <button type="button" className="map-control-btn" onClick={handleZoomIn} title="Zoom In">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
              <button type="button" className="map-control-btn" onClick={handleZoomOut} title="Zoom Out">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>

            <div className="map-control control-group">
              <button type="button" className="map-control-btn" onClick={handleFullscreen} title="Fullscreen">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
